import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export type QualityLevel = "good" | "poor" | "bad" | "unknown";

interface ConnectionQualityProps {
    /** The peer connections to monitor. When empty/null, shows the local estimate. */
    pcs: Record<string, RTCPeerConnection>;
    /** Class name for the indicator container. */
    className?: string;
}

/**
 * Teams-style 3-bar signal-strength icon that reflects real-time WebRTC
 * connection quality. Uses RTCInboundRtpStreamStats.jitter and packetsLost
 * to classify quality as good / poor / bad.
 *
 * Thresholds (aligned with libwebrtc's own UI thresholds):
 *   Good : jitter < 30 ms AND loss < 2 %
 *   Poor : jitter < 100 ms AND loss < 8 %
 *   Bad  : everything else
 */
export function ConnectionQualityIndicator({ pcs, className }: ConnectionQualityProps) {
    const quality = useConnectionQuality(pcs);
    return <SignalBars quality={quality} className={className} />;
}

/** Standalone bars component for composition. */
export function SignalBars({ quality, className }: { quality: QualityLevel; className?: string }) {
    const color =
        quality === "good" ? "text-emerald-400" :
        quality === "poor" ? "text-amber-400" :
        quality === "bad"  ? "text-red-400" : "text-white/30";

    const label =
        quality === "good" ? "Connection: Good" :
        quality === "poor" ? "Connection: Poor" :
        quality === "bad"  ? "Connection: Bad" : "Connection: Unknown";

    return (
        <div
            className={cn("flex items-end gap-[2px]", color, className)}
            role="img"
            aria-label={label}
            title={label}
        >
            {/* 3-bar signal icon — bars grow in height left-to-right */}
            <Bar active={quality !== "unknown"} height="h-1.5" />
            <Bar active={quality === "poor" || quality === "good"} height="h-2.5" />
            <Bar active={quality === "good"} height="h-3.5" />
        </div>
    );
}

function Bar({ active, height }: { active: boolean; height: string }) {
    return (
        <span
            className={cn(
                "inline-block w-1 rounded-[1px] transition-opacity",
                height,
                active ? "opacity-100 bg-current" : "opacity-30 bg-current"
            )}
        />
    );
}

/** Polls WebRTC stats every 2 s and returns the worst-case quality across all pcs. */
function useConnectionQuality(pcs: Record<string, RTCPeerConnection>): QualityLevel {
    const [quality, setQuality] = useState<QualityLevel>("unknown");
    const pcKeys = Object.keys(pcs).sort().join(",");

    useEffect(() => {
        if (pcKeys.length === 0) {
            setQuality("unknown");
            return;
        }

        let cancelled = false;

        const tick = async () => {
            let worstJitter = 0;
            let totalLost = 0;
            let totalReceived = 0;
            let hasStat = false;

            for (const pc of Object.values(pcs)) {
                if (pc.connectionState !== "connected") continue;
                try {
                    const report = await pc.getStats();
                    report.forEach((stat: RTCStats & Record<string, unknown>) => {
                        if (stat.type === "inbound-rtp") {
                            hasStat = true;
                            const jitter = typeof stat["jitter"] === "number" ? stat["jitter"] as number * 1000 : 0;
                            if (jitter > worstJitter) worstJitter = jitter;
                            totalLost += typeof stat["packetsLost"] === "number" ? stat["packetsLost"] as number : 0;
                            totalReceived += typeof stat["packetsReceived"] === "number" ? stat["packetsReceived"] as number : 0;
                        }
                    });
                } catch {
                    // ignore closing connections
                }
            }

            if (!hasStat || cancelled) return;

            const totalPackets = totalLost + totalReceived;
            const lossPct = totalPackets > 0 ? totalLost / totalPackets : 0;

            let level: QualityLevel;
            if (worstJitter < 30 && lossPct < 0.02) {
                level = "good";
            } else if (worstJitter < 100 && lossPct < 0.08) {
                level = "poor";
            } else {
                level = "bad";
            }

            if (!cancelled) setQuality(level);
        };

        void tick();
        const handle = window.setInterval(() => void tick(), 2_000);
        return () => {
            cancelled = true;
            window.clearInterval(handle);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pcKeys]);

    return quality;
}
